// =============================================================================
// PERSONA ENTITY MODEL
// =============================================================================

// Decision dimension types (7 dimensions, 5 levels each)
export type ClaimOwnership = 'hedged' | 'personal' | 'observed' | 'general' | 'universal';
export type Confidence = 'tentative' | 'exploratory' | 'steady' | 'confident' | 'authoritative';
export type CallToAction = 'none' | 'open' | 'invite' | 'recommend' | 'urge';
export type Vulnerability = 'guarded' | 'selective' | 'balanced' | 'open' | 'exposing';
export type Energy = 'restrained' | 'calm' | 'engaged' | 'enthusiastic' | 'intense';
export type Formality = 'formal' | 'professional' | 'conversational' | 'casual' | 'colloquial';
export type Specificity = 'abstract' | 'conceptual' | 'balanced' | 'specific' | 'granular';

export interface PersonaDecisions {
  claimOwnership: ClaimOwnership;
  confidence: Confidence;
  callToAction: CallToAction;
  vulnerability: Vulnerability;
  energy: Energy;
  formality: Formality;
  specificity: Specificity;
}

export interface PersonaWorldview {
  coreBeliefs: string[];
  values: string[];
  audience: string;
  purpose: string;
}

export interface PersonaVoice {
  traits: string[];
  description: string;
  examplePhrases: string[];
  avoidPhrases: string[];
}

// Main Persona interface for wizards
export interface WizardPersona {
  // Identity
  name: string;
  avatar: string;
  tagline: string;

  // Worldview
  worldview: PersonaWorldview;

  // Voice
  voice: PersonaVoice;

  // Decisions (7 dimensions)
  decisions: PersonaDecisions;

  // Meta
  archetype?: string;
}

// =============================================================================
// DECISION DIMENSION METADATA
// =============================================================================

export interface DecisionLevel {
  value: string;
  label: string;
  description: string;
}

export interface DecisionDimension {
  key: keyof PersonaDecisions;
  question: string;
  levels: DecisionLevel[];
}

export const DECISION_DIMENSIONS: DecisionDimension[] = [
  {
    key: 'claimOwnership',
    question: 'When sharing ideas, how does this persona frame them?',
    levels: [
      { value: 'hedged', label: 'As hunches', description: '"I wonder if..." / "Some say..."' },
      {
        value: 'personal',
        label: 'As personal experience',
        description: '"In my experience..." / "What worked for me..."',
      },
      {
        value: 'observed',
        label: 'As patterns noticed',
        description: '"I\'ve seen this work..." / "People find..."',
      },
      {
        value: 'general',
        label: 'As general guidance',
        description: '"This tends to work..." / "The evidence suggests..."',
      },
      {
        value: 'universal',
        label: 'As established truth',
        description: '"This is how it works." / "The truth is..."',
      },
    ],
  },
  {
    key: 'confidence',
    question: 'How certain does this persona sound?',
    levels: [
      {
        value: 'tentative',
        label: 'Still figuring it out',
        description: '"I\'m experimenting..." / "Maybe..."',
      },
      {
        value: 'exploratory',
        label: 'Thinking out loud',
        description: '"I think..." / "I\'m exploring..."',
      },
      { value: 'steady', label: 'Grounded in experience', description: '"I\'ve found that..."' },
      {
        value: 'confident',
        label: 'Sure of themselves',
        description: '"I know this works..." / "Trust me..."',
      },
      {
        value: 'authoritative',
        label: 'Definitive authority',
        description: '"Here\'s the truth..." / "This is definitive."',
      },
    ],
  },
  {
    key: 'callToAction',
    question: 'Does this persona ask the audience to do anything?',
    levels: [
      { value: 'none', label: 'No, just sharing', description: 'No call to action' },
      {
        value: 'open',
        label: 'Opens conversation',
        description: '"What do you think?" / "Anyone else?"',
      },
      {
        value: 'invite',
        label: 'Gentle invitation',
        description: '"Check it out if you\'re curious..."',
      },
      {
        value: 'recommend',
        label: 'Clear recommendation',
        description: '"You should try this..."',
      },
      { value: 'urge', label: 'Strong push', description: '"You need this." / "Do this now."' },
    ],
  },
  {
    key: 'vulnerability',
    question: 'How much does this persona share their struggles?',
    levels: [
      {
        value: 'guarded',
        label: 'Never — all wins',
        description: 'Projects strength and certainty',
      },
      {
        value: 'selective',
        label: 'Rarely — strategic only',
        description: "Mentions past struggles they've overcome",
      },
      { value: 'balanced', label: 'Sometimes — mixed', description: 'Mix of wins and challenges' },
      {
        value: 'open',
        label: 'Often — open journey',
        description: 'Freely shares doubts and learning',
      },
      {
        value: 'exposing',
        label: 'Always — leads with struggle',
        description: 'Raw, vulnerable, unfiltered',
      },
    ],
  },
  {
    key: 'energy',
    question: "What's the energy level?",
    levels: [
      { value: 'restrained', label: 'Restrained', description: 'Calm, measured, understated' },
      { value: 'calm', label: 'Calm', description: 'Even-keeled, steady' },
      { value: 'engaged', label: 'Engaged', description: 'Present, interested, attentive' },
      {
        value: 'enthusiastic',
        label: 'Enthusiastic',
        description: 'Excited, expressive, energetic',
      },
      { value: 'intense', label: 'Intense', description: 'Passionate, emphatic, high energy' },
    ],
  },
  {
    key: 'formality',
    question: 'How does this persona talk?',
    levels: [
      { value: 'formal', label: 'Formal', description: 'Academic, structured, no contractions' },
      {
        value: 'professional',
        label: 'Professional',
        description: 'Polished, business-appropriate',
      },
      {
        value: 'conversational',
        label: 'Conversational',
        description: 'Friendly, natural, approachable',
      },
      { value: 'casual', label: 'Casual', description: 'Relaxed, informal' },
      {
        value: 'colloquial',
        label: 'Like texting a friend',
        description: 'Slang, emoji, "omg bestie"',
      },
    ],
  },
  {
    key: 'specificity',
    question: 'How specific does this persona get?',
    levels: [
      {
        value: 'abstract',
        label: 'Big picture only',
        description: 'Principles and philosophy, no details',
      },
      { value: 'conceptual', label: 'Mostly conceptual', description: 'Ideas with light context' },
      {
        value: 'balanced',
        label: 'Balanced',
        description: 'Mix of principles and practical details',
      },
      {
        value: 'specific',
        label: 'Actionable specifics',
        description: 'Clear, concrete recommendations',
      },
      {
        value: 'granular',
        label: 'Highly detailed',
        description: 'Precise numbers, steps, protocols',
      },
    ],
  },
];

// =============================================================================
// DEFAULT VALUES
// =============================================================================

export const defaultDecisions: PersonaDecisions = {
  claimOwnership: 'personal',
  confidence: 'steady',
  callToAction: 'open',
  vulnerability: 'balanced',
  energy: 'engaged',
  formality: 'conversational',
  specificity: 'balanced',
};

export const defaultWorldview: PersonaWorldview = {
  coreBeliefs: [],
  values: [],
  audience: '',
  purpose: '',
};

export const defaultVoice: PersonaVoice = {
  traits: [],
  description: '',
  examplePhrases: [],
  avoidPhrases: [],
};

export const defaultPersona: WizardPersona = {
  name: '',
  avatar: '',
  tagline: '',
  worldview: { ...defaultWorldview },
  voice: { ...defaultVoice },
  decisions: { ...defaultDecisions },
};

// =============================================================================
// CONSTANTS
// =============================================================================

export const CHANNELS = [
  'LinkedIn',
  'Twitter/X',
  'Blog',
  'Newsletter',
  'WhatsApp',
  'YouTube',
  'Discord',
  'Instagram',
  'TikTok',
  'Podcast',
] as const;

export const CONTENT_TYPES = [
  'Long-form articles',
  'Short posts',
  'Threads',
  'Video scripts',
  'Email sequences',
  'Documentation',
  'Social captions',
  'Comments & replies',
] as const;

// Personality traits suggestions (for voice.traits)
export const TRAIT_SUGGESTIONS = [
  'Authoritative',
  'Approachable',
  'Analytical',
  'Creative',
  'Empathetic',
  'Direct',
  'Humorous',
  'Professional',
  'Warm',
  'Visionary',
  'Practical',
  'Inspiring',
  'Witty',
  'Patient',
  'Bold',
] as const;

// Sample prompts for testing
export const SAMPLE_PROMPTS = [
  'The future of remote work',
  'Why authenticity beats perfection',
  'Lessons learned from failure',
  'The power of compound thinking',
  'Building in public: pros and cons',
] as const;

// =============================================================================
// ARCHETYPES
// =============================================================================

export interface PersonaArchetype {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  persona: Partial<WizardPersona>;
  exampleOutput: string;
}

export const ARCHETYPES: PersonaArchetype[] = [
  {
    id: 'thought-leader',
    name: 'Thought Leader',
    description:
      'Strategic, authoritative voice for establishing industry expertise and sharing forward-thinking perspectives.',
    icon: 'lightbulb',
    color: 'primary',
    persona: {
      tagline: 'Shaping the future of [industry]',
      worldview: {
        coreBeliefs: ['The future belongs to those who see it first', 'Ideas compound over time'],
        values: ['Innovation', 'Insight', 'Impact'],
        audience: 'Industry professionals and decision-makers',
        purpose: 'To challenge conventional thinking and illuminate new paths forward',
      },
      voice: {
        traits: ['Authoritative', 'Visionary', 'Direct'],
        description:
          'Uses data to support bold claims, shares personal experiences, connects dots others miss',
        examplePhrases: ["Here's what most people miss...", 'The real opportunity is...'],
        avoidPhrases: ["I'm not sure but...", 'This might work...'],
      },
      decisions: {
        claimOwnership: 'general',
        confidence: 'authoritative',
        callToAction: 'recommend',
        vulnerability: 'selective',
        energy: 'engaged',
        formality: 'professional',
        specificity: 'conceptual',
      },
    },
    exampleOutput:
      'The future of content creation lies not in producing more, but in transforming single insights into contextually-adapted expressions across multiple channels.',
  },
  {
    id: 'educator',
    name: 'Educator',
    description:
      'Clear, patient voice for explaining complex concepts and helping others learn effectively.',
    icon: 'book-open',
    color: 'info',
    persona: {
      tagline: 'Making [topic] accessible to everyone',
      worldview: {
        coreBeliefs: ['Everyone can learn with the right approach', 'Clarity beats complexity'],
        values: ['Patience', 'Clarity', 'Empowerment'],
        audience: 'Learners at all levels seeking to understand complex topics',
        purpose: 'To break down barriers to understanding and celebrate learning wins',
      },
      voice: {
        traits: ['Approachable', 'Patient', 'Clear'],
        description: 'Uses analogies, breaks down complexity, celebrates small wins',
        examplePhrases: ['Think of it like...', "Here's a simple way to understand this..."],
        avoidPhrases: ['Obviously...', 'As everyone knows...'],
      },
      decisions: {
        claimOwnership: 'observed',
        confidence: 'steady',
        callToAction: 'invite',
        vulnerability: 'open',
        energy: 'engaged',
        formality: 'conversational',
        specificity: 'specific',
      },
    },
    exampleOutput:
      'Think of personas like filters for a camera—same scene, different looks. Your core message stays the same, but the presentation adapts to each audience.',
  },
  {
    id: 'brand-voice',
    name: 'Brand Voice',
    description:
      'Consistent, polished voice that maintains brand identity across all communications.',
    icon: 'briefcase',
    color: 'success',
    persona: {
      tagline: 'The voice of [brand]',
      worldview: {
        coreBeliefs: ['Consistency builds trust', 'Every touchpoint matters'],
        values: ['Reliability', 'Professionalism', 'Trust'],
        audience: 'Customers and stakeholders who value dependability',
        purpose: 'To represent the brand authentically and build lasting relationships',
      },
      voice: {
        traits: ['Professional', 'Consistent', 'Trustworthy'],
        description: 'On-brand messaging, benefits-focused, clear call-to-actions',
        examplePhrases: ['We believe...', 'Our commitment to you...'],
        avoidPhrases: ['I personally think...', 'Just my opinion...'],
      },
      decisions: {
        claimOwnership: 'general',
        confidence: 'confident',
        callToAction: 'recommend',
        vulnerability: 'guarded',
        energy: 'engaged',
        formality: 'professional',
        specificity: 'balanced',
      },
    },
    exampleOutput:
      'At HalfNote, we believe your ideas deserve to reach every corner of your audience—without the cognitive tax of manual adaptation.',
  },
  {
    id: 'community-builder',
    name: 'Community Builder',
    description:
      'Warm, inclusive voice for fostering engagement and building authentic connections.',
    icon: 'users',
    color: 'warning',
    persona: {
      tagline: 'Building communities around [passion]',
      worldview: {
        coreBeliefs: ['Together we go further', 'Every voice matters'],
        values: ['Inclusion', 'Connection', 'Authenticity'],
        audience: 'Community members seeking belonging and shared growth',
        purpose: 'To foster genuine connections and celebrate collective wins',
      },
      voice: {
        traits: ['Warm', 'Inclusive', 'Engaging'],
        description: 'Asks questions, celebrates members, shares behind-the-scenes',
        examplePhrases: ['What do you think?', "I'd love to hear from you..."],
        avoidPhrases: ['You should...', 'The right way is...'],
      },
      decisions: {
        claimOwnership: 'personal',
        confidence: 'steady',
        callToAction: 'open',
        vulnerability: 'open',
        energy: 'enthusiastic',
        formality: 'casual',
        specificity: 'balanced',
      },
    },
    exampleOutput:
      "What if you could spend more time forming ideas and less time reformatting them? That's the question that sparked our journey. What's yours?",
  },
  {
    id: 'storyteller',
    name: 'Storyteller',
    description:
      'Narrative-driven voice that transforms information into compelling, memorable stories.',
    icon: 'book',
    color: 'secondary',
    persona: {
      tagline: 'Stories that resonate and inspire',
      worldview: {
        coreBeliefs: ['Stories change minds', 'Emotion drives action'],
        values: ['Authenticity', 'Connection', 'Impact'],
        audience: 'Anyone who wants to feel something, not just learn something',
        purpose: 'To transform information into experiences that stick',
      },
      voice: {
        traits: ['Creative', 'Empathetic', 'Vivid'],
        description: 'Uses narrative arcs, sensory details, emotional hooks',
        examplePhrases: ['Picture this...', 'That moment when...'],
        avoidPhrases: ['In conclusion...', 'To summarize...'],
      },
      decisions: {
        claimOwnership: 'personal',
        confidence: 'steady',
        callToAction: 'none',
        vulnerability: 'exposing',
        energy: 'enthusiastic',
        formality: 'conversational',
        specificity: 'specific',
      },
    },
    exampleOutput:
      "It was 3 AM when the realization hit me: I'd spent more time reformatting content than creating it. That moment changed everything.",
  },
  {
    id: 'provocateur',
    name: 'Provocateur',
    description: 'Bold, contrarian voice that challenges conventional thinking and sparks debate.',
    icon: 'zap',
    color: 'error',
    persona: {
      tagline: 'Questioning everything in [field]',
      worldview: {
        coreBeliefs: ['Conventional wisdom is often wrong', 'Discomfort drives growth'],
        values: ['Truth', 'Courage', 'Independence'],
        audience: 'People tired of the same recycled advice',
        purpose: 'To challenge assumptions and spark genuine thinking',
      },
      voice: {
        traits: ['Bold', 'Contrarian', 'Direct'],
        description: 'Challenges assumptions, uses rhetorical questions, takes strong positions',
        examplePhrases: ['Hot take:', 'Unpopular opinion:', "Here's what nobody tells you..."],
        avoidPhrases: ['Everyone agrees...', 'The safe choice is...'],
      },
      decisions: {
        claimOwnership: 'universal',
        confidence: 'authoritative',
        callToAction: 'urge',
        vulnerability: 'guarded',
        energy: 'intense',
        formality: 'casual',
        specificity: 'conceptual',
      },
    },
    exampleOutput:
      "Hot take: Most 'thought leadership' content is just regurgitated wisdom wrapped in new buzzwords. Here's what actually moves the needle...",
  },
];

// =============================================================================
// MOCK CONTENT GENERATION
// =============================================================================

export interface MockContentParams {
  persona: Partial<WizardPersona>;
  channel: string;
  topic?: string;
}

// Helper to get formality descriptor
const getFormalityDescriptor = (formality: Formality): string => {
  switch (formality) {
    case 'formal':
      return 'formal';
    case 'professional':
      return 'professional';
    case 'conversational':
      return 'approachable';
    case 'casual':
      return 'casual';
    case 'colloquial':
      return 'friendly';
    default:
      return 'balanced';
  }
};

// Helper to get CTA based on decision
const getCallToActionText = (cta: CallToAction, topic: string): string => {
  switch (cta) {
    case 'none':
      return '';
    case 'open':
      return `What patterns are you seeing in ${topic}?`;
    case 'invite':
      return `Curious to explore this further? Check it out.`;
    case 'recommend':
      return `You should seriously consider this approach.`;
    case 'urge':
      return `Do this now. You'll thank me later.`;
    default:
      return '';
  }
};

// Helper to get opener based on confidence
const getOpener = (confidence: Confidence, claimOwnership: ClaimOwnership): string => {
  if (confidence === 'authoritative' && claimOwnership === 'universal') {
    return "Here's the truth:";
  }
  if (confidence === 'confident') {
    return "I've learned that";
  }
  if (confidence === 'steady') {
    return 'In my experience,';
  }
  if (confidence === 'exploratory') {
    return "I've been thinking about";
  }
  return 'I wonder if';
};

// Mock content templates
export const mockContentTemplates: Record<
  string,
  (params: MockContentParams) => { content: string; highlights: string[] }
> = {
  linkedin: ({ persona, topic }) => {
    const name = persona.name || 'Your Persona';
    const traits = persona.voice?.traits || [];
    const tagline = persona.tagline || 'industry expert';
    const decisions = persona.decisions || defaultDecisions;

    const opener = getOpener(decisions.confidence, decisions.claimOwnership);
    const cta = getCallToActionText(decisions.callToAction, topic || 'your industry');

    const content = `As a ${tagline}, ${opener.toLowerCase()} ${topic || 'the future of our industry'} deserves more attention.

${traits.length > 0 ? `My ${traits[0]?.toLowerCase()} perspective tells me that` : 'I believe that'} the real opportunity isn't in following trends—it's in understanding the underlying shifts that create them.

Here's what most people miss:

1. The surface-level changes are distractions
2. The structural changes are opportunities
3. Those who adapt early compound their advantage

${persona.voice?.examplePhrases?.[0] || cta}

#ThoughtLeadership #${name.replace(/\s/g, '')}`;

    return {
      content,
      highlights: [decisions.confidence, decisions.claimOwnership],
    };
  },

  twitter: ({ persona, topic }) => {
    const traits = persona.voice?.traits || [];
    const decisions = persona.decisions || defaultDecisions;

    const prefix =
      decisions.confidence === 'authoritative'
        ? 'Hot take:'
        : decisions.confidence === 'tentative'
          ? 'Thinking out loud:'
          : traits[0]
            ? `${traits[0]} take:`
            : 'Observation:';

    const style = getFormalityDescriptor(decisions.formality);

    const content = `${prefix} ${topic || 'Content creation'} is about to change forever.

Most people are preparing for the wrong future.

Here's the ${style} truth nobody's talking about:

(thread)`;

    return {
      content,
      highlights: [decisions.confidence, decisions.formality],
    };
  },

  blog: ({ persona, topic }) => {
    const name = persona.name || 'the author';
    const traits = persona.voice?.traits || [];
    const decisions = persona.decisions || defaultDecisions;

    const vulnerabilityOpener =
      decisions.vulnerability === 'exposing'
        ? `I'll be honest—I've struggled with ${topic || 'this'} more than I'd like to admit.`
        : decisions.vulnerability === 'open'
          ? `Like many of you, I've had my share of challenges with ${topic || 'this'}.`
          : '';

    const content = `# Why ${name} Believes in ${topic || 'The Power of Authentic Voice'}

*${persona.tagline || 'A perspective on modern content creation'}*

---

${vulnerabilityOpener}

In a world drowning in content, standing out requires more than volume—it demands authenticity.

${traits.length > 0 ? `Drawing on a ${traits.join(', ').toLowerCase()} approach,` : ''} this piece explores the fundamental shift happening in how we communicate ideas.

## The Challenge

Every creator faces the same paradox: the platforms multiply, but time doesn't.

## The Insight

${persona.voice?.examplePhrases?.[0] || "The best content doesn't try to be everywhere—it tries to be meaningful somewhere."}

---

*Continue reading for the full framework...*`;

    return {
      content,
      highlights: traits,
    };
  },

  newsletter: ({ persona, topic }) => {
    const name = persona.name || 'a thoughtful creator';
    const traits = persona.voice?.traits || [];
    const decisions = persona.decisions || defaultDecisions;

    const greeting =
      decisions.formality === 'colloquial'
        ? 'Hey!'
        : decisions.formality === 'casual'
          ? 'Hey there,'
          : decisions.formality === 'formal'
            ? 'Dear reader,'
            : 'Hi there,';

    const signoff =
      decisions.formality === 'colloquial'
        ? 'Talk soon,'
        : decisions.formality === 'casual'
          ? 'Cheers,'
          : 'Best regards,';

    const warmIntro =
      decisions.vulnerability === 'open' || decisions.vulnerability === 'exposing'
        ? 'I wanted to share this personally because I know many of you are wrestling with the same questions.'
        : decisions.energy === 'enthusiastic'
          ? "I'm excited to share something that's been on my mind."
          : 'Let me get straight to the point.';

    const content = `Subject: ${topic || 'This changed how I think about content'}

${greeting}

${name} here with something that's been on my mind.

${warmIntro}

**The big idea:** ${topic || 'Your voice is your competitive advantage'}

${persona.voice?.examplePhrases?.[0] || "The creators who win aren't the loudest—they're the most consistent."}

Here's what I'm experimenting with:
- Testing new formats
- Doubling down on what resonates
- Letting go of what doesn't

${decisions.callToAction === 'open' ? "Hit reply and tell me—what's working for you?" : decisions.callToAction === 'urge' ? 'Try this today. Seriously.' : 'More on this next week.'}

${signoff}
${persona.name || 'Your Name'}`;

    return {
      content,
      highlights: ['Personal touch', ...traits.slice(0, 2)],
    };
  },

  whatsapp: ({ persona, topic }) => {
    const traits = persona.voice?.traits || [];
    const decisions = persona.decisions || defaultDecisions;

    const greeting =
      decisions.formality === 'colloquial'
        ? 'Hey! 👋'
        : decisions.formality === 'casual'
          ? 'Hey there!'
          : decisions.formality === 'formal'
            ? 'Hello,'
            : 'Hi!';

    const opener = getOpener(decisions.confidence, decisions.claimOwnership);
    const cta = getCallToActionText(decisions.callToAction, topic || 'this');

    const emoji =
      decisions.energy === 'intense' || decisions.energy === 'enthusiastic'
        ? ' 🔥'
        : decisions.energy === 'engaged'
          ? ' 💡'
          : '';

    const content = `${greeting}

${opener} ${topic || 'something interesting'} is worth talking about.${emoji}

${persona.voice?.examplePhrases?.[0] || 'Quick thought:'}

The key insight? ${traits.length > 0 ? `Being ${traits[0]?.toLowerCase()}` : 'Staying authentic'} makes all the difference.

${cta ? `\n${cta}` : ''}

${decisions.formality === 'colloquial' ? 'lmk what you think! 💬' : decisions.formality === 'casual' ? 'Let me know your thoughts!' : 'Would love to hear your perspective.'}`;

    return {
      content,
      highlights: [decisions.confidence, decisions.formality],
    };
  },
};

// Generate mock content for a channel
export function generateMockContent(params: MockContentParams): {
  content: string;
  highlights: string[];
} {
  const channelKey = params.channel.toLowerCase().replace(/[/\s]/g, '');
  const template = mockContentTemplates[channelKey] || mockContentTemplates.linkedin;
  return template(params);
}
